
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import AdvancedLogoUpload from "@/components/ui/advanced-logo-upload";
import ProfilePhotoUpload from "@/components/ui/profile-photo-upload";
import { useAuth } from "@/contexts/AuthContext";

interface CompanyCreationFormProps {
  firstName: string;
  setFirstName: (value: string) => void;
  lastName: string;
  setLastName: (value: string) => void;
  companyName: string;
  setCompanyName: (value: string) => void;
  companyRole: string;
  setCompanyRole: (value: string) => void;
  personalPhoneNumber: string;
  setPersonalPhoneNumber: (value: string) => void;
  website: string;
  setWebsite: (value: string) => void;
  streetAddress: string;
  setStreetAddress: (value: string) => void;
  suburb: string;
  setSuburb: (value: string) => void;
  city: string;
  setCity: (value: string) => void;
  provinceState: string;
  setProvinceState: (value: string) => void;
  postalCode: string;
  setPostalCode: (value: string) => void;
  setLogoUrl: (url: string | null) => void;
  setProfilePhotoUrl: (url: string | null) => void;
  isLoading: boolean;
  onSubmit: (e: React.FormEvent) => void;
}

const CompanyCreationForm = (props: CompanyCreationFormProps) => {
  const { user } = useAuth();
  return (
    <form onSubmit={props.onSubmit} className="space-y-6">
      <div className="space-y-4">
        <div className="text-center">
          <h3 className="text-lg font-medium mb-4">Your Profile Photo</h3>
          <ProfilePhotoUpload onImageUpload={props.setProfilePhotoUrl} userId={user?.id} />
        </div>

        <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="firstName">First Name *</Label>
            <Input
              id="firstName"
              value={props.firstName}
              onChange={(e) => props.setFirstName(e.target.value)}
              required
              placeholder="Your first name"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="lastName">Last Name *</Label>
            <Input
              id="lastName"
              value={props.lastName}
              onChange={(e) => props.setLastName(e.target.value)}
              required
              placeholder="Your last name"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="personalPhoneNumber">Personal Phone Number *</Label>
          <Input
            id="personalPhoneNumber"
            value={props.personalPhoneNumber}
            onChange={(e) => props.setPersonalPhoneNumber(e.target.value)}
            required
            placeholder="Your personal phone number"
            type="tel"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="companyRole">Your Role *</Label>
          <Input
            id="companyRole"
            value={props.companyRole}
            onChange={(e) => props.setCompanyRole(e.target.value)}
            placeholder="e.g. CEO, Founder, Manager"
            required
          />
        </div>
      </div>

      <div className="border-t pt-6">
        <h3 className="text-lg font-medium mb-4">Company Information</h3>
        
        <div className="space-y-4">
          <div className="text-center">
            <Label className="text-sm font-medium mb-4 block">Company Logo (Optional)</Label>
            <AdvancedLogoUpload onImageUpload={props.setLogoUrl} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="companyName">Company Name *</Label>
            <Input
              id="companyName"
              value={props.companyName}
              onChange={(e) => props.setCompanyName(e.target.value)}
              required
              placeholder="Your company name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="website">Website (Optional)</Label>
            <Input
              id="website"
              value={props.website}
              onChange={(e) => props.setWebsite(e.target.value)}
              placeholder="www.yourcompany.com or yourcompany.co.za"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="streetAddress">Street Address (Optional)</Label>
            <Input
              id="streetAddress"
              value={props.streetAddress}
              onChange={(e) => props.setStreetAddress(e.target.value)}
              placeholder="123 Main Street"
            />
          </div>

          <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="suburb">Suburb (Optional)</Label>
              <Input
                id="suburb"
                value={props.suburb}
                onChange={(e) => props.setSuburb(e.target.value)}
                placeholder="Suburb"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="city">City (Optional)</Label>
              <Input
                id="city"
                value={props.city}
                onChange={(e) => props.setCity(e.target.value)}
                placeholder="City"
              />
            </div>
          </div>

          <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="provinceState">Province/State (Optional)</Label>
              <Input
                id="provinceState"
                value={props.provinceState}
                onChange={(e) => props.setProvinceState(e.target.value)}
                placeholder="Province or State"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="postalCode">Postal Code (Optional)</Label>
              <Input
                id="postalCode"
                value={props.postalCode}
                onChange={(e) => props.setPostalCode(e.target.value)}
                placeholder="Postal Code"
              />
            </div>
          </div>
        </div>
      </div>

      <Button type="submit" className="w-full" disabled={props.isLoading}>
        {props.isLoading ? "Creating Company..." : "Create Company & Complete Profile"}
      </Button>
    </form>
  );
};

export default CompanyCreationForm;
